import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { type InteractionSoundKind, interactionSoundProps } from "../interaction-sound";
import { cn } from "../lib/cn";

/*
 * IconButton, the shared icon-only button the system was missing. Recommendation
 * 12 of the 2026-06-12 visual-language audit flagged the feedback thumbs and a
 * few other icon-only controls hand-rolling a raw `<button>` because the shared
 * Button is text-shaped (horizontal padding, a `<span>` content wrapper). This
 * primitive owns the icon-button interaction contract once: square footprint,
 * focus ring, press feedback, disabled handling, and a required aria-label.
 *
 * Variants:
 *   - soft     · bordered paper chip, the default resting control
 *   - ghost    · no chrome until hover, for dense toolbars / close buttons
 *   - unstyled · shape + interaction only; the caller drives colour via
 *                className (for toggle / on-dark state matrices the named
 *                variants would only fight)
 */
export type IconButtonSize = "sm" | "md" | "lg";
export type IconButtonVariant = "soft" | "ghost" | "unstyled";

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: the control is icon-only, so it must carry an accessible name. */
  label: string;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  /** Interaction kind for the cue; defaults to "action". "dismiss" for a close X. */
  soundKind?: InteractionSoundKind;
  children: ReactNode;
}

const BASE =
  "inline-flex shrink-0 items-center justify-center rounded-full [transition:background-color_180ms_var(--ease-out-expo),border-color_180ms_var(--ease-out-expo),color_180ms_var(--ease-out-expo),transform_120ms_var(--ease-out-expo)] active:scale-[0.96] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:cursor-not-allowed disabled:opacity-60";

const SIZES: Record<IconButtonSize, string> = {
  sm: "h-7 w-7",
  md: "h-8 w-8",
  lg: "h-10 w-10",
};

const VARIANTS: Record<IconButtonVariant, string> = {
  soft: "border border-mist bg-paper text-gray-500 hover:border-teal-600 hover:text-teal-700",
  ghost: "text-gray-500 hover:bg-mist hover:text-gray-700",
  unstyled: "border",
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = "md", variant = "soft", soundKind, className, children, type, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      aria-label={label}
      title={rest.title ?? label}
      disabled={disabled}
      {...interactionSoundProps(disabled ? "none" : (soundKind ?? "action"))}
      className={cn(BASE, SIZES[size], VARIANTS[variant], className)}
      {...rest}
    >
      {children}
    </button>
  );
});
